import { useState, useEffect } from "react";
import { useSignal, useSignalEffect } from "@preact/signals-react";
import { v4 as uuidv4 } from "uuid";
import { setupHandlers, setupLocalState } from "../../library/dataService";
import { INGRichTextEditorProps } from "../../library/NGFieldExtensions";
import {
  camelCaseToTitleCase,
  createAutocompleteConfigAndContext,
  formatFileName,
  getRepositoryAssetsBaseUrl,
  getTempFileName,
  getTestId,
} from "../../library/utils";
import { useDynamicImport } from "../../hooks/useDynamicImport";

import type FroalaEditor from "froala-editor";

// For tribute
// import Tribute from "tributejs";

import { client } from "../../library/nats-client";
import { Buffer } from "buffer";
import NGLayoutItem from "../../generators/NGLayoutItem";

window.Buffer = Buffer;

type Editor = typeof FroalaEditor;

enum UploadType {
  Image = "image",
  File = "file",
  Video = "video",
}

export default function NGRichTextEditor({ config, context }: INGRichTextEditorProps) {
  const local = setupLocalState(
    config,
    {
      TributeRows: useSignal(config.TributeRows ?? []),
      TributeSearchString: useSignal(config.TributeSearchString ?? []),

      Sequence: useSignal(config.Sequence ?? 0),
      Name: useSignal(config.Name ?? ""),
      Visible: useSignal(config.Visible ?? true),

      ValidationRules: useSignal(config.ValidationRules ?? []),
      ContextAutocomplete: useSignal(config.ContextAutocomplete ?? null),

      Actions: useSignal(config.Actions ?? []),
      Bindings: useSignal(config.Bindings ?? {}),

      Value: useSignal(config.Value ?? ""),
      Disabled: useSignal(config.Disabled ?? false),

      Style: useSignal(config.Style ?? {}),
      Classes: useSignal(config.Classes ?? ""),

      ToolbarInline: useSignal(config.ToolbarInline ?? false),
      ToolbarButtons: useSignal(config.ToolbarButtons ?? []),

      ImageAllowedTypes: useSignal(config.ImageAllowedTypes ?? []),
      ImageDefaultWidth: useSignal(config.ImageDefaultWidth ?? 0),
      ImageInsertButtons: useSignal(config.ImageInsertButtons ?? []),
      ImageMaxSize: useSignal(config.ImageMaxSize ?? 0),

      VideoUpload: useSignal(config.VideoUpload ?? false),
      VideoAllowedTypes: useSignal(config.VideoAllowedTypes ?? []),
      VideoDefaultWidth: useSignal(config.VideoDefaultWidth ?? 0),
      VideoInsertButtons: useSignal(config.VideoInsertButtons ?? []),
      VideoDefaultDisplay: useSignal(config.VideoDefaultDisplay ?? ""),

      FileAllowedTypes: useSignal(config.FileAllowedTypes ?? []),
      FileMaxSize: useSignal(config.FileMaxSize ?? 0),

      LinkInsertButtons: useSignal(config.LinkInsertButtons ?? []),
      LinkEditButtons: useSignal(config.LinkEditButtons ?? []),

      CharCounterMax: useSignal(config.CharCounterMax ?? 0),
      CharCounterCount: useSignal(config.CharCounterCount ?? false),

      PlaceholderText: useSignal(config.PlaceholderText ?? ""),

      QuickInsertButtons: useSignal(config.QuickInsertButtons ?? []),
      QuickInsertEnabled: useSignal(config.QuickInsertEnabled ?? false),
      QuickInsertTags: useSignal(config.QuickInsertTags ?? []),

      Height: useSignal(config.Height ?? 0),
      HeightMax: useSignal(config.HeightMax ?? 0),
      HeightMin: useSignal(config.HeightMin ?? 0),
    },
    context
  );
  const Froala: any = useSignal(null);

  useEffect(() => {
    const loadModules = async () => {
      await import("@/lib/froala/style.css");
      const { default: FroalaEditor } = await import("@/lib/froala/froala-bundle.es.js");
      Froala.value = FroalaEditor;
    };

    loadModules();
  }, []);
  const ready = useSignal(false);
  const fileName = useSignal<string | null>(null);
  const editor = useSignal<Editor>({} as Editor);
  const addedLinks = useSignal<Map<string, string>>(new Map());
  const handlers = setupHandlers(config, context);

  const validId = /^[a-zA-Z_]/.test(config.Id) ? config.Id : `id-${config.Id}`;

  // const tribute = config.SupportTributes
  //   ? new Tribute({
  //       values: (text: string, cb: (values: any[]) => void) => {
  //         local.TributeSearchString.value = text;

  //         console.log("Tribute values called with text:", text, filteredUsers.value);
  //         //cb(filteredUsers.value);
  //         abc.value = cb;
  //       },
  //       selectTemplate: function (item) {
  //         console.log("Selected item:", item);
  //         return (
  //           '<span class="fr-deletable fr-tribute" style="z-index: 9999;height:500px;background-color:red"><a>' +
  //           item.original.key +
  //           "</a></span>"
  //         );
  //       },
  //     })
  //   : null;

  // const filteredUsers = useComputed(() => {
  //   //if (!config.SupportTributes || !local.TributeRows.value || local.TributeRows.value.length === 0) return [];

  //   const t =
  //     local.TributeRows.value?.map((row) => ({
  //       key: row.Name,
  //       value: row.Name,
  //       // email: row.Email,
  //       id: row.Id,
  //     })) ?? [];

  //   console.log("XXXTribute values callback called with users:", t, tribute?.isActive);

  //   //tribute?.showMenuForCollection(editorRef.value as any);
  //   //tribute?.append(0, t);
  //   abc.value(t);
  // });

  // const filteredUsers = useComputed(
  //   () =>
  //     local.TributeRows.value.filter((user) =>
  //       user.key.toLowerCase().includes(local.TributeSearchString.value.toLowerCase())
  //     ),
  //   [local.TributeRows, local.TributeSearchString]
  // );

  const saveOnAssets = async (file, type) => {
    fileName.value = file.name || `print-Screen-${uuidv4()}.png`;
    const id = getTempFileName(file.type);
    const reader = file.stream().getReader();
    const stream = new ReadableStream({
      start(controller) {
        return (function pump() {
          return reader.read().then(({ done, value }) => {
            if (done) {
              controller.close();
              return;
            }
            controller.enqueue(value);
            return pump();
          });
        })();
      },
    });
    try {
      const objPutResponse = await client.objPut("temp", id, "Temp file", file.size, stream);
      if (!objPutResponse.success) {
        const msg = objPutResponse.reasons?.map((x) => x.message).join("\n");
        console.error("Error", msg);
      } else {
        const response = await client.request("project.asset.save", {
          Description: file.name,
          File: {
            Id: id,
            ContentType: file.type,
            LastModified: file.lastModifiedDate,
            Name: fileName.value,
            Type: "limbo",
            Size: file.size,
          },
          Path: null,
          Type: camelCaseToTitleCase(type),
        });
        if (!response.success) {
          const msg = response.reasons?.map((x) => x.message).join("\n");
          console.error("Error", msg);
          return;
        }
        return fileName.value;
      }
    } catch (error) {
      console.error("Unexpected Error", error);
    }
  };

  const formatAssetUrl = async (file, uploadType: UploadType) => {
    const fileNameFormatted = formatFileName(fileName.value);
    const extension = fileName.value?.split(".").pop();
    const assetUrl = getRepositoryAssetsBaseUrl();
    if (file.src) {
      file.src = "";
      file.src = `${assetUrl}${camelCaseToTitleCase(uploadType)}s/${fileNameFormatted}.${extension}`;
    } else {
      file.href = "";
      file.href = `${assetUrl}${camelCaseToTitleCase(uploadType)}s/${fileNameFormatted}.${extension}`;
    }
  };
  const toolbarButtonsMap = {
    bold: "Bold",
    italic: "Italic",
    underline: "Underline",
    strikeThrough: "Strikethrough",
    insertImage: "Insert Image",
    insertVideo: "Insert Video",
    insertLink: "Insert Link",
    insertFile: "Insert File",
    alignLeft: "Align Left",
    alignCenter: "Align Center",
    alignRight: "Align Right",
    alignJustify: "Align Justify",
    undo: "Undo",
    redo: "Redo",
    indent: "Increase Indent",
    outdent: "Decrease Indent",
    formatOL: "Ordered List",
    formatUL: "Unordered List",
    fullscreen: "Fullscreen",
    html: "HTML View",
    fontSize: "Font Size",
    fontFamily: "Font Family",
    textColor: "Text Color",
  };

  const getOptionKeys = (humanReadableArray) => {
    const reverseMap = Object.fromEntries(Object.entries(toolbarButtonsMap).map(([key, value]) => [value, key]));
    return humanReadableArray.map((label) => reverseMap[label]);
  };

  const imageConfigs = {
    imageUpload: local.ToolbarButtons.value?.includes("Insert Image"),
    imageInsertButtons: local.ImageInsertButtons.value,
    imageAllowedTypes: local.ImageAllowedTypes.value,
    imageMaxSize: local.ImageMaxSize.value,
    imageDefaultWidth: local.ImageDefaultWidth.value,
  };

  const toolbarButtons = local.ToolbarButtons.value ? getOptionKeys(local.ToolbarButtons.value) : [];

  const toolbarConfigs = {
    toolbarInline: local.ToolbarInline.value,
    toolbarVisibleWithoutSelection: false,
    // toolbarSticky: local.ToolbarSticky.value,
    toolbarButtons: toolbarButtons,
    // toolbarButtonsXS: local.ToolbarButtonsXS.value,
    // toolbarButtonsSM: local.ToolbarButtonsSM.value,
    // toolbarButtonsMD: local.ToolbarButtonsMD.value,
  };

  const videoConfigs = {
    videoUpload: local.ToolbarButtons.value?.includes("Insert Video"),
    videoAllowedTypes: local.VideoAllowedTypes.value,
    videoDefaultWidth: local.VideoDefaultWidth.value,
    videoInsertButtons: local.VideoInsertButtons.value,
    videoDefaultDisplay: local.VideoDefaultDisplay.value,
  };

  const fileConfigs = {
    fileUpload: local.ToolbarButtons.value?.includes("Insert File"),
    fileAllowedTypes: local.FileAllowedTypes.value,
    fileMaxSize: local.FileMaxSize.value,
  };

  const linkConfigs = {
    linkInsertButtons: local.LinkInsertButtons.value,
    linkEditButtons: local.LinkEditButtons.value,
  };

  const editorConfigs = {
    charCounterMax: local.CharCounterMax.value,
    charCounterCount: local.CharCounterCount.value ?? false,
    placeholderText: local.PlaceholderText.value,
    quickInsertButtons: local.QuickInsertButtons.value,
    quickInsertEnabled: local.QuickInsertEnabled.value,
    quickInsertTags: local.QuickInsertTags.value,
    // pastePlain: local.PastePlain.value,
    // tabSpaces: local.TabSpaces.value,
    height: local.Height.value,
    heightMin: local.HeightMin.value,
    heightMax: local.HeightMax.value,
    key: import.meta.env.VITE_FROALA_KEY,
    attribution: false,
    imageUploadRemoteUrls: false,
    pluginsEnabled: [
      "image",
      "video",
      "file",
      "link",
      "quickInsert",
      "charCounter",
      "draggable",
      "embedly",
      "align",
      "colors",
      "fontSize",
      "fontFamily",
      "lists",
      "fullscreen",
    ],
  };

  const createLinkMap = (links) => {
    const linkMap = new Map<string, string>();
    links.forEach((link) => {
      linkMap.set(link.href, link);
    });
    return linkMap;
  };

  const getNewLinks = (links) => links.filter((link) => !addedLinks.value.has(link.href));

  const getFileType = (file: File) => {
    const type = file.type.split("/")[0];
    return type !== UploadType.Image && type !== UploadType.Video ? UploadType.File : type;
  };

  const handleBeforeUpload = async (files) => {
    const uploadType = getFileType(files[0]);
    if (uploadType === UploadType.File) addedLinks.value = createLinkMap(Array.from(editor.value.doc.links));
    const file = files[0];
    if (file) {
      await saveOnAssets(file, uploadType);
      editor.value.events.trigger(`${uploadType}.uploaded`, null, true);
    }
  };

  const handleFileUploaded = () => {
    const links = Array.from(editor.value.doc.links);
    const newLinks = getNewLinks(links);
    const fileUploaded = newLinks[0];
    const assetUrl = getRepositoryAssetsBaseUrl();
    if (!fileUploaded.href.includes(assetUrl)) {
      formatAssetUrl(fileUploaded, UploadType.File);
    }
  };

  const handleVideoUploaded = () => {
    const filesList = editor.value[UploadType.Video].get();
    const fileUploaded = filesList[0].children[0];
    const assetUrl = getRepositoryAssetsBaseUrl();
    if (!fileUploaded.src.includes(assetUrl)) {
      formatAssetUrl(fileUploaded, UploadType.Video);
    }
  };

  const handleImageUploaded = () => {
    const filesList = editor.value[UploadType.Image].get();
    const fileUploaded = filesList[0];
    const assetUrl = getRepositoryAssetsBaseUrl();
    if (!fileUploaded.src.includes(assetUrl)) {
      formatAssetUrl(fileUploaded, UploadType.Image);
    }
  };

  const handleUploaded = (uploadType: UploadType) => {
    if (uploadType === UploadType.File) handleFileUploaded();
    if (uploadType === UploadType.Video) handleVideoUploaded();
    if (uploadType === UploadType.Image) handleImageUploaded();
  };

  type DropkiqUI = { menuIsOpen: () => boolean };
  const dropkiqUI = useSignal<DropkiqUI>({} as DropkiqUI);
  const setDropkiqUI = (ui) => {
    dropkiqUI.value = ui;
  };

  function initialize() {
    editorRef.value = this.el;
    this.html.set(local.Value.value || "");

    // if (config.SupportTributes && tribute) {
    //   tribute.attach(editorRef.value);
    // }

    this.events.on(
      "keydown",
      function (e) {
        if (e.which == Froala.value.KEYCODE.ENTER && dropkiqUI.value.menuIsOpen()) {
          return false;
        }
        // else if (e.which == FroalaEditor.KEYCODE.ENTER && tribute && tribute.isActive) {
        //   return false;
        //}
      },
      true
    );
    ready.value = true;
  }

  const editorRef = useSignal<HTMLDivElement | null>(null);
  const eventConfigs = {
    events: {
      "image.beforeUpload": handleBeforeUpload,
      "image.uploaded": () => handleUploaded(UploadType.Image),
      "file.beforeUpload": handleBeforeUpload,
      "file.uploaded": () => handleUploaded(UploadType.File),
      "video.beforeUpload": handleBeforeUpload,
      "video.uploaded": () => handleUploaded(UploadType.Video),
      contentChanged: function () {
        const newValue = editor.value.html.get();
        local.Value.value = newValue;
        if (handlers.onChange) {
          handlers.onChange(null, newValue);
        }
      },
      initialized: initialize,
    },
  };

  const [editorKey, setEditorKey] = useState(0);
  useEffect(() => {
    setEditorKey((prev) => prev + 1);
  }, [config]);

  const getDefinedValues = (values) => {
    const definedValues = {};
    Object.keys(values).forEach((key) => {
      if (Array.isArray(values[key]) && values[key].length > 0) {
        definedValues[key] = values[key];
      } else if (!Array.isArray(values[key]) && values[key] !== undefined) {
        definedValues[key] = values[key];
      }
    });
    return definedValues;
  };

  const froalaConfig = {
    ...getDefinedValues(imageConfigs),
    ...getDefinedValues(eventConfigs),
    ...getDefinedValues(toolbarConfigs),
    ...getDefinedValues(videoConfigs),
    ...getDefinedValues(fileConfigs),
    ...getDefinedValues(linkConfigs),
    ...getDefinedValues(editorConfigs),
  };

  useSignalEffect(() => {
    if (!ready.value) return; // ← skip until init
    const htmlApi = editor.value?.html;
    const newVal = local.Value.value || "";

    if (htmlApi && htmlApi.get() !== newVal) {
      htmlApi.set(newVal);
    }
  });
  useSignalEffect(() => {
    const froala = Froala.value;
    if (froala) {
      editor.value = new (froala as any)(`#${validId}`, {
        model: local.Value.value,
        ...froalaConfig,
        events: { ...eventConfigs.events },
      });
    }
  });

  const { currentConfig, currentContext } = createAutocompleteConfigAndContext(
    local.ContextAutocomplete.value,
    context,
    { elementRef: editorRef.value, setDropkiqUI }
  );

  return (
    <div key={editorKey} id={validId} data-testid={getTestId(config)} data-type={config.__typename}>
      {editorRef.value && local.ContextAutocomplete.value && (
        <NGLayoutItem config={currentConfig} context={currentContext} />
      )}
    </div>
  );
}
